import {Injectable} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';

import {Patient} from '../../../types';


/**
 * Service for creating and configuring patient forms.
 * Handles form initialization, validator updates, and default values.
 */
@Injectable()
export class PatientFormBuilderService {
  /**
   * Fields that are conditionally required based on visibility.
   */
  private readonly conditionalRequiredFields = ['gender', 'firstName', 'lastName', 'birthDate', 'customerNr'];

  /**
   * Default values for standard fields when hidden.
   */
  private readonly fieldDefaults: {[key: string]: any} = {
    gender: 'male',
    firstName: '-',
    lastName: '-',
    birthDate: '1900-01-01',
    zipCode: '',
    city: '',
    street: '',
    streetNr: '',
    address: '',
    email: '',
    location: '',
    pseudoId: '',
    customerNr: '-',
    caseNr: '',
    status: '',
    locale: 'de_DE',
    allowMail: false,
    prefill: false,
    anonymous: false,
    pseudonymous: false
  };

  /**
   * Injects dependencies.
   */
  public constructor(
    private formBuilder: FormBuilder
  ) {
  }

  /**
   * Creates the main patient form with all standard fields.
   *
   * @returns FormGroup with patient fields.
   */
  public createPatientForm(): FormGroup {
    const fb = this.formBuilder;

    return fb.group({
      'uid': fb.control(null),
      'gender': fb.control(null, Validators.required),
      'firstName': fb.control(null, Validators.required),
      'lastName': fb.control(null, Validators.required),
      'zipCode': fb.control(null),
      'city': fb.control(null),
      'street': fb.control(null),
      'streetNr': fb.control(null),
      'address': fb.control(null),
      'birthDate': fb.control(null, Validators.required),
      'location': fb.control(null),
      'email': fb.control(null),
      'allowMail': fb.control('false'),
      'locale': fb.control('de_DE'),
      'prefill': fb.control('no'),
      'status': fb.control(null),
      'pseudoId': fb.control(null),
      'customerNr': fb.control(null, Validators.required),
      'caseNr': fb.control(null),
      'anonymous': fb.control(null),
      'pseudonymous': fb.control(null)
    });
  }

  /**
   * Updates form validators based on which fields are visible.
   * Only visible fields have their required validators applied.
   *
   * @param form The patient FormGroup.
   * @param visibleFields Array of visible field keys.
   */
  public updateValidatorsForLayout(form: FormGroup, visibleFields: string[]): void {
    for (const fieldKey of this.conditionalRequiredFields) {
      const control = form.get(fieldKey);
      if (control) {
        if (visibleFields.includes(fieldKey)) {
          control.setValidators(Validators.required);
        } else {
          control.clearValidators();
        }
        control.updateValueAndValidity();
      }
    }
  }

  /**
   * Applies default values for fields that are not visible in the current layout.
   * This ensures all required fields have values even when hidden from the form.
   *
   * @param patient The patient object to apply defaults to.
   * @param visibleFields Array of visible field keys.
   * @returns The patient object with defaults applied.
   */
  public applyDefaultsForHiddenFields(patient: Patient, visibleFields: string[]): Patient {
    for (const [fieldKey, defaultValue] of Object.entries(this.fieldDefaults)) {
      if (!visibleFields.includes(fieldKey)) {
        // field is not visible, apply default if current value is null/undefined
        if (patient[fieldKey] === null || patient[fieldKey] === undefined) {
          patient[fieldKey] = defaultValue;
        }
      }
    }

    return patient;
  }

  /**
   * Prepares patient data for saving by converting form values.
   *
   * @param patient The patient object from form values.
   * @returns The patient object with converted values.
   */
  public prepareForSave(patient: Patient): Patient {
    // convert allowMail string to boolean
    if (patient.allowMail && (<any>patient.allowMail) === 'true') {
      patient.allowMail = true;
    }

    // convert prefill string to boolean flags
    if (patient.prefill) {
      const prefillValue = <any>patient.prefill;
      if (prefillValue === 'no') {
        patient.prefill = false;
        patient.prefillOptional = false;
      } else if (prefillValue === 'yes') {
        patient.prefill = true;
        patient.prefillOptional = false;
      } else if (prefillValue === 'optional') {
        patient.prefill = true;
        patient.prefillOptional = true;
      }
    }

    return patient;
  }

  /**
   * Resets the form for anonymous allocation mode.
   *
   * @param form The patient FormGroup.
   * @param isAnonymous Whether anonymous mode is active.
   */
  public resetForAnonymous(form: FormGroup, isAnonymous: boolean): void {
    form.reset();
    form.get('anonymous').setValue(isAnonymous);
    form.get('allowMail').setValue('false');
    form.get('prefill').setValue('no');

    if (!isAnonymous) {
      form.get('gender').setValue('male');
    }
  }

  /**
   * Resets the form for pseudonymous allocation mode.
   *
   * @param form The patient FormGroup.
   * @param isPseudonymous Whether pseudonymous mode is active.
   */
  public resetForPseudonymous(form: FormGroup, isPseudonymous: boolean): void {
    form.reset();
    form.get('pseudonymous').setValue(isPseudonymous);
    form.get('allowMail').setValue('false');
    form.get('prefill').setValue('no');

    if (!isPseudonymous) {
      form.get('gender').setValue('male');
    }
  }

  /**
   * Gets the list of conditionally required fields.
   *
   * @returns Array of field keys.
   */
  public getConditionalRequiredFields(): string[] {
    return [...this.conditionalRequiredFields];
  }

  /**
   * Gets the default value for a field.
   *
   * @param fieldKey The field key.
   * @returns The default value or undefined.
   */
  public getFieldDefault(fieldKey: string): any {
    return this.fieldDefaults[fieldKey];
  }
}

